/**
 * ExportSettingsModal
 * Configure export settings and themes
 */

import { useState } from 'react'
import {
  Modal,
  ModalHeader,
  ModalTitle,
  ModalBody,
  ModalFooter,
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../ui'
import { ExportSettings, ExportTheme, EXPORT_THEMES, DEFAULT_EXPORT_SETTINGS } from '@/core/models'
import { Check, Download } from 'lucide-react'
import { clsx } from 'clsx'

export interface ExportSettingsModalProps {
  open: boolean
  onClose: () => void
  onExport: (format: 'pdf' | 'epub' | 'markdown' | 'html', settings: ExportSettings) => void
  defaultSettings?: ExportSettings
}

export function ExportSettingsModal({
  open,
  onClose,
  onExport,
  defaultSettings = DEFAULT_EXPORT_SETTINGS,
}: ExportSettingsModalProps) {
  const [settings, setSettings] = useState<ExportSettings>(defaultSettings)
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'epub' | 'markdown' | 'html'>('pdf')

  const handleThemeSelect = (theme: ExportTheme) => {
    setSettings({ ...settings, theme })
  }

  const handleExport = () => {
    onExport(selectedFormat, settings)
    onClose()
  }

  const formats = [
    { id: 'pdf' as const, name: 'PDF', description: 'Portable Document Format' },
    { id: 'epub' as const, name: 'EPUB', description: 'E-reader compatible' },
    { id: 'markdown' as const, name: 'Markdown', description: 'Plain text format' },
    { id: 'html' as const, name: 'HTML', description: 'Web page format' },
  ]

  return (
    <Modal open={open} onClose={onClose} size="xl">
      <ModalHeader>
        <ModalTitle>Export Settings</ModalTitle>
      </ModalHeader>
      <ModalBody>
        <div className="space-y-6">
          {/* Format Selection */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3">Select Format</h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {formats.map(format => (
                <button
                  key={format.id}
                  onClick={() => setSelectedFormat(format.id)}
                  className={clsx(
                    'p-3 rounded-lg border-2 transition-all text-left',
                    selectedFormat === format.id
                      ? 'border-blue-500 bg-blue-50'
                      : 'border-gray-200 hover:border-blue-300',
                  )}
                >
                  <div className="font-medium text-sm">{format.name}</div>
                  <div className="text-xs text-gray-600 mt-1">{format.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Theme Selection - Only for PDF and HTML */}
          {(selectedFormat === 'pdf' || selectedFormat === 'html') && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Choose Theme</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[250px] overflow-y-auto pr-2">
                {EXPORT_THEMES.map(theme => (
                  <Card
                    key={theme.id}
                    padding="sm"
                    hover
                    onClick={() => handleThemeSelect(theme)}
                    className={clsx(
                      'cursor-pointer transition-all',
                      settings.theme.id === theme.id
                        ? 'ring-2 ring-blue-500 bg-blue-50'
                        : 'hover:border-blue-300',
                    )}
                  >
                    <CardHeader className="mb-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1">
                          <CardTitle className="text-sm">{theme.name}</CardTitle>
                          <CardDescription className="text-xs mt-1">
                            {theme.description}
                          </CardDescription>
                        </div>
                        {settings.theme.id === theme.id && (
                          <div className="bg-blue-500 rounded-full p-1">
                            <Check className="h-3 w-3 text-white" />
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-xs space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Font:</span>
                          <span style={{ fontFamily: theme.fonts.body }} className="text-gray-700">
                            {theme.fonts.body.split(',')[0]}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">Size:</span>
                          <span className="text-gray-700">{theme.pageSize.toUpperCase()}</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {/* Export Options - Only for PDF */}
          {selectedFormat === 'pdf' && (
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-3">Options</h3>
              <div className="space-y-3">
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeCoverPage}
                    onChange={e =>
                      setSettings({ ...settings, includeCoverPage: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Include Cover Page</div>
                    <div className="text-xs text-gray-600">
                      Add a professional cover page with title and author
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeTableOfContents}
                    onChange={e =>
                      setSettings({ ...settings, includeTableOfContents: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Table of Contents</div>
                    <div className="text-xs text-gray-600">
                      Generate a clickable table of contents
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includePageNumbers}
                    onChange={e =>
                      setSettings({ ...settings, includePageNumbers: e.target.checked })
                    }
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Page Numbers</div>
                    <div className="text-xs text-gray-600">Add page numbers to footer</div>
                  </div>
                </label>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.includeFooters}
                    onChange={e => setSettings({ ...settings, includeFooters: e.target.checked })}
                    className="h-4 w-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Footer Text</div>
                    <div className="text-xs text-gray-600">
                      Include "Created with Ebook Genius" in footer
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={handleExport}>
          <Download className="h-4 w-4 mr-2" />
          Export as {selectedFormat.toUpperCase()}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
